import * as yup from 'yup';
import { PIX_KEY_TYPE_VALUES } from '../constants/pixKeyTypeConstants';
import { VALID_CNAE_CODES } from '../constants/cnaeConstants';
import { FORM_MODES } from '../constants/formModeConstants';
import { validateCNPJ, validateCPF } from '@wareclouds/frontend-common';

export const createBankAccountSchema = (t) => {
  return yup.object().shape({
    formMode: yup.string().oneOf([FORM_MODES.CNPJ, FORM_MODES.CPF]).required(),

    document: yup
      .string()
      .required(t('validation:bank_info.document.required'))
      .test(
        'document-valid',
        t('validation:bank_info.document.invalid'),
        function (value) {
          if (!value) return false;
          const formMode =
            this?.parent?.formMode || this?.options?.context?.formMode;
          return formMode === FORM_MODES.CPF
            ? validateCPF(value)
            : validateCNPJ(value);
        }
      ),

    legalName: yup.string().when('formMode', {
      is: FORM_MODES.CNPJ,
      then: (schema) =>
        schema.required(t('validation:bank_info.legal_name.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    fiscalAddress: yup.string().when('formMode', {
      is: FORM_MODES.CNPJ,
      then: (schema) =>
        schema.required(t('validation:bank_info.fiscal_address.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    cnae: yup
      .object()
      .nullable()
      .when('formMode', {
        is: FORM_MODES.CNPJ,
        then: (schema) =>
          schema
            .required(t('validation:bank_info.cnae.required'))
            .test(
              'valid-cnae',
              t('validation:bank_info.cnae.invalid'),
              (value) => value && VALID_CNAE_CODES.has(value.value)
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    bank: yup
      .object()
      .nullable()
      .required(t('validation:bank_info.bank.required'))
      .test('has-label', t('validation:bank_info.bank.invalid'), (value) => {
        return value && value.label && value.label.trim() !== '';
      }),

    accountType: yup
      .object()
      .nullable()
      .required(t('validation:bank_info.account_type.required'))
      .test(
        'has-label',
        t('validation:bank_info.account_type.invalid'),
        (value) => {
          return value && value.label && value.label.trim() !== '';
        }
      ),

    agencyCode: yup
      .string()
      .required(t('validation:bank_info.agency_code.required'))
      .min(3, t('validation:bank_info.agency_code.min_length', { min: 3 }))
      .max(5, t('validation:bank_info.agency_code.max_length', { max: 5 }))
      .matches(/^\d{3,5}$/, t('validation:bank_info.agency_code.format')),

    accountNumber: yup
      .string()
      .required(t('validation:bank_info.account_number.required'))
      .min(5, ({ min }) =>
        t('validation:bank_info.account_number.min_length', { min })
      )
      .max(15, ({ max }) =>
        t('validation:bank_info.account_number.max_length', { max })
      )
      .matches(
        /^\d{5,13}(-\d)?$/,
        t('validation:bank_info.account_number.format')
      ),

    pixKeyType: yup
      .object()
      .nullable()
      .required(t('validation:bank_info.pix_key_type.required'))
      .test(
        'has-label',
        t('validation:bank_info.pix_key_type.invalid'),
        (value) => {
          return value && value.label && value.label.trim() !== '';
        }
      ),

    pixKey: yup
      .string()
      .required(t('validation:bank_info.pix_key.required'))
      .when('pixKeyType', {
        is: (pixKeyType) => pixKeyType?.value === PIX_KEY_TYPE_VALUES.EMAIL,
        then: (schema) =>
          schema.email(t('form:bank_info.pix_key.invalid_email')),
      })
      .when('pixKeyType', {
        is: (pixKeyType) => pixKeyType?.value === PIX_KEY_TYPE_VALUES.CPF,
        then: (schema) =>
          schema
            .matches(
              /^(\d{3}\.?\d{3}\.?\d{3}-?\d{2}|\d{11})$/,
              t('form:bank_info.pix_key.invalid_cpf_format')
            )
            .test(
              'valid-cpf',
              t('form:bank_info.pix_key.invalid_cpf'),
              validateCPF
            ),
      })
      .when('pixKeyType', {
        is: (pixKeyType) => pixKeyType?.value === PIX_KEY_TYPE_VALUES.CNPJ,
        then: (schema) =>
          schema
            .matches(
              /^(\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}|\d{14})$/,
              t('form:bank_info.pix_key.invalid_cnpj_format')
            )
            .test(
              'valid-cnpj',
              t('form:bank_info.pix_key.invalid_cnpj'),
              validateCNPJ
            ),
      })
      .when('pixKeyType', {
        is: (pixKeyType) => pixKeyType?.value === PIX_KEY_TYPE_VALUES.PHONE,
        then: (schema) =>
          schema.test(
            'valid-phone',
            t('form:bank_info.pix_key.invalid_phone'),
            (value) => {
              if (!value) return false;
              const digits = value.replace(/\D/g, '');
              return /^\d{10,11}$/.test(digits);
            }
          ),
      })
      .when('pixKeyType', {
        is: (pixKeyType) => pixKeyType?.value === PIX_KEY_TYPE_VALUES.RANDOM,
        then: (schema) =>
          schema.test(
            'valid-random',
            t('form:bank_info.pix_key.invalid_random_format'),
            (value) => {
              if (!value) return false;
              const isUuid =
                /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
                  value
                ) && value.length === 36;
              const isNumeric = /^\d+$/.test(value);
              return isUuid || isNumeric;
            }
          ),
      }),
  });
};

export const initialValues = {
  formMode: FORM_MODES.CNPJ,
  document: '',
  legalName: '',
  fiscalAddress: '',
  cnae: null,
  bank: null,
  accountType: null,
  agencyCode: '',
  accountNumber: '',
  pixKeyType: null,
  pixKey: '',
};
